// OpenAI Chat Service 集成
import { API_CONFIG } from './apiConfig.js'

export class OpenAIChatService {
  constructor() {
    this.endpoint = API_CONFIG.api.chat
    this.debugLogs = API_CONFIG.debug.enableLogs
    this.conversationHistory = []
    this.maxHistoryLength = 20 // 保留最近20轮对话
  }

  /**
   * 生成智能回复
   * @param {string} userMessage - 用户消息
   * @param {string} language - 当前语言 ('cantonese' | 'english')
   * @param {Object} context - 上下文信息
   */
  async generateIntelligentResponse(userMessage, language = 'cantonese', context = {}) {
    try {
      if (this.debugLogs) {
        console.log('🤖 开始调用聊天接口...', {
          message: userMessage.substring(0, 100),
          language,
          historyLength: this.conversationHistory.length
        })
      }

      const requestBody = {
        message: userMessage,
        language,
        // 最近几轮对话；系统提示词由服务端 functions/api/chat.js 负责
        history: this.getRecentHistory()
      }

      if (this.debugLogs) {
        console.log('📤 发送到 /api/chat:', { historyLength: requestBody.history.length })
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Chat API错误 ${response.status}: ${errorData.error || 'Unknown error'}`)
      }

      const data = await response.json()
      
      if (!data.response) {
        throw new Error('Chat API返回了空的回应')
      }

      const aiResponse = data.response
      
      // 保存到对话历史
      this.addToHistory('user', userMessage)
      this.addToHistory('assistant', aiResponse)

      if (this.debugLogs) {
        console.log('✅ OpenAI回应成功:', {
          responseLength: aiResponse.length,
          tokensUsed: data.tokensUsed || 'unknown'
        })
      }

      // 分析回应并生成相关操作
      const actions = this.generateResponseActions(aiResponse, language)

      return {
        response: aiResponse,
        actions: actions,
        metadata: {
          model: data.model,
          tokensUsed: data.tokensUsed,
          language: language
        }
      }

    } catch (error) {
      console.error('❌ OpenAI Chat Service调用失败:', error)
      
      // 返回友好的错误回应
      const fallbackResponse = language === 'cantonese' 
        ? '唔好意思，我而家遇到咗技術問題，未能即時回應您嘅查詢。請稍候再試，或者直接聯絡我哋嘅專業團隊獲得幫助。'
        : 'I apologize, but I\'m experiencing technical difficulties and cannot respond to your inquiry right now. Please try again later or contact our professional team directly for assistance.'

      return {
        response: fallbackResponse,
        actions: this.generateContactActions(language),
        error: true,
        errorMessage: error.message
      }
    }
  }

  /**
   * 添加消息到对话历史
   */
  addToHistory(role, content) {
    this.conversationHistory.push({
      role: role,
      content: content,
      timestamp: new Date()
    })

    // 限制历史长度
    if (this.conversationHistory.length > this.maxHistoryLength) {
      this.conversationHistory = this.conversationHistory.slice(-this.maxHistoryLength)
    }
  }

  /**
   * 获取最近的对话历史（用于上下文）
   */
  getRecentHistory(limit = 8) {
    return this.conversationHistory
      .slice(-limit)
      .map(item => ({
        role: item.role,
        content: item.content
      }))
  }

  /**
   * 根据回应内容生成相关操作按钮
   */
  generateResponseActions(response, language) {
    const actions = []
    const lowerResponse = response.toLowerCase()

    // 检测服务相关内容
    if (lowerResponse.includes('服務') || lowerResponse.includes('service') || 
        lowerResponse.includes('business') || lowerResponse.includes('商業')) {
      actions.push({
        id: 'view_services',
        type: 'link',
        label: language === 'cantonese' ? '瀏覽服務' : 'View Services',
        style: 'primary',
        url: '/service'
      })
    }

    // 检测联系相关内容
    if (lowerResponse.includes('聯絡') || lowerResponse.includes('contact') || 
        lowerResponse.includes('聯繫') || lowerResponse.includes('專業團隊')) {
      actions.push({
        id: 'contact_us',
        type: 'link', 
        label: language === 'cantonese' ? '聯絡我們' : 'Contact Us',
        style: 'success',
        url: '/contact'
      })
    }

    // 检测公司信息相关内容
    if (lowerResponse.includes('關於') || lowerResponse.includes('about') || 
        lowerResponse.includes('公司') || lowerResponse.includes('company')) {
      actions.push({
        id: 'about_company',
        type: 'link',
        label: language === 'cantonese' ? '關於AMIS' : 'About AMIS',
        style: 'secondary',
        url: '/about'
      })
    }

    return actions
  }

  /**
   * 生成联系操作按钮（错误时使用）
   */
  generateContactActions(language) {
    return [{
      id: 'contact_support',
      type: 'link',
      label: language === 'cantonese' ? '聯絡技術支援' : 'Contact Support',
      style: 'warning',
      url: '/contact'
    }]
  }

  /**
   * 清除对话历史
   */
  clearHistory() {
    this.conversationHistory = []
    if (this.debugLogs) {
      console.log('🗑️ 对话历史已清除')
    }
  }

  /**
   * 获取对话统计
   */
  getConversationStats() {
    return {
      totalMessages: this.conversationHistory.length,
      userMessages: this.conversationHistory.filter(msg => msg.role === 'user').length,
      assistantMessages: this.conversationHistory.filter(msg => msg.role === 'assistant').length,
      oldestMessage: this.conversationHistory.length > 0 ? this.conversationHistory[0].timestamp : null,
      newestMessage: this.conversationHistory.length > 0 ? this.conversationHistory[this.conversationHistory.length - 1].timestamp : null
    }
  }

  /**
   * 验证服务配置（前端无密钥；服务端配置状态用 checkServerHealth() 查询）
   */
  validateConfiguration() {
    if (!this.endpoint) {
      throw new Error('Chat Endpoint配置缺失')
    }
    return true
  }
}

// 创建单例实例
export const openaiChatService = new OpenAIChatService()